import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { handleAPIError } from '../utils/errors';
import { useToast } from '../context';

const defaultSelect = (response) => ({
    data: Array.isArray(response?.data) ? response.data : (response?.data ?? []),
});

async function runListQuery({
    queryFnRef,
    selectRef,
    showErrorToastRef,
    showErrorRef,
    setData,
    setMeta,
    setStatus,
    setError,
    setHasFetched,
}) {
    setStatus('loading');
    setError(null);

    try {
        const response = await queryFnRef.current();
        const { data: nextData, meta: nextMeta } = selectRef.current(response);
        setData(nextData ?? []);
        setMeta(nextMeta ?? null);
        setStatus('success');
        setHasFetched(true);
    } catch (err) {
        const errorInfo = handleAPIError(err);
        setError(errorInfo);
        setStatus('error');
        setHasFetched(true);

        if (showErrorToastRef.current) {
            showErrorRef.current(errorInfo.message);
        }
    }
}

/**
 * @param {Object} options
 * @param {unknown[]} options.queryKey
 * @param {() => Promise<unknown>} options.queryFn
 * @param {(response: unknown) => { data: unknown[], meta?: unknown }} [options.select]
 * @param {boolean} [options.enabled]
 * @param {boolean} [options.showErrorToast]
 * @param {boolean} [options.keepPreviousData]
 */
export function useListQuery({
    queryKey,
    queryFn,
    select = defaultSelect,
    enabled = true,
    showErrorToast = true,
    keepPreviousData = false,
}) {
    const { showError } = useToast();
    const [data, setData] = useState([]);
    const [meta, setMeta] = useState(null);
    const [status, setStatus] = useState('pending');
    const [error, setError] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const queryKeySerialized = useMemo(() => JSON.stringify(queryKey), [queryKey]);
    const queryFnRef = useRef(queryFn);
    const selectRef = useRef(select);
    const showErrorToastRef = useRef(showErrorToast);
    const showErrorRef = useRef(showError);

    queryFnRef.current = queryFn;
    selectRef.current = select;
    showErrorToastRef.current = showErrorToast;
    showErrorRef.current = showError;

    const isLoading = status === 'loading';
    const isRefetching = isLoading && hasFetched;
    const isPending = !enabled || (!hasFetched && (status === 'pending' || status === 'loading'));
    const isEmpty = hasFetched && status === 'success' && data.length === 0;
    const isError = hasFetched && status === 'error' && data.length === 0;
    const hasData = data.length > 0;

    const refetch = useCallback(async () => {
        if (!enabled) {
            return;
        }

        await runListQuery({
            queryFnRef,
            selectRef,
            showErrorToastRef,
            showErrorRef,
            setData,
            setMeta,
            setStatus,
            setError,
            setHasFetched,
        });
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            setStatus('pending');
            setHasFetched(false);
            setData([]);
            setMeta(null);
            setError(null);
            return undefined;
        }

        if (!keepPreviousData) {
            setStatus('pending');
            setHasFetched(false);
            setData([]);
            setMeta(null);
            setError(null);
        }

        runListQuery({
            queryFnRef,
            selectRef,
            showErrorToastRef,
            showErrorRef,
            setData,
            setMeta,
            setStatus,
            setError,
            setHasFetched,
        });

        return undefined;
    }, [queryKeySerialized, enabled, keepPreviousData]);

    return {
        data,
        setData,
        meta,
        setMeta,
        isPending,
        isLoading,
        isRefetching,
        isEmpty,
        hasData,
        hasFetched,
        isError,
        error,
        refetch,
    };
}

export default useListQuery;
